import React from 'react';

// Simplified schematic (not anatomically precise) front/back body diagrams for tagging
// gym-session focus areas on the Week Schedule sheet. Each zone is a clickable region that
// toggles into `value.front` / `value.back` — a flat array of zone ids per side, no
// left/right leg distinction (matches the manual sheet's red-boxed regions, which were
// never split by side either).

// Body-region granularity was originally per-muscle (quadriceps, hamstrings, glutes, etc.)
// but that was too specific for how the sheet actually gets used — collapsed to a single
// upper/lower split per side (2026-07-25).
// Exported so /print/week-schedule can redraw the same silhouettes with fixed (theme-
// independent) colors — print pages in this app never use the var(--...) theme tokens,
// since printing under a dark-mode session would otherwise print a dark diagram.
export const FRONT_ZONES = [
  { id: 'upper_body', label: 'Upper body', rects: [{ x: 33, y: 50, w: 134, h: 120 }] },
  { id: 'lower_body', label: 'Lower body', rects: [{ x: 65, y: 170, w: 70, h: 180 }] },
];

export const BACK_ZONES = [
  { id: 'upper_body', label: 'Upper body', rects: [{ x: 33, y: 50, w: 134, h: 120 }] },
  { id: 'lower_body', label: 'Lower body', rects: [{ x: 65, y: 170, w: 70, h: 180 }] },
];
// Outline paths (head/torso/arms/legs) are also shared verbatim by the print renderer.
export const BODY_OUTLINE_PATHS = {
  circle: { cx: 100, cy: 32, r: 22 },
  neck: { x: 92, y: 50, width: 16, height: 16, rx: 4 },
  torso: 'M 60 64 L 140 64 L 130 170 L 70 170 Z',
  armLeft: { x: 33, y: 66, width: 22, height: 112, rx: 10 },
  armRight: { x: 145, y: 66, width: 22, height: 112, rx: 10 },
  legLeft: { x: 65, y: 170, width: 32, height: 180, rx: 12 },
  legRight: { x: 103, y: 170, width: 32, height: 180, rx: 12 },
  footLeft: { cx: 79, cy: 357, rx: 15, ry: 8 },
  footRight: { cx: 121, cy: 357, rx: 15, ry: 8 },
};

function Silhouette({ side, zones, active, onToggle }) {
  return (
    <svg viewBox="0 0 200 420" width="100%" height={260} style={{ display: 'block' }}>
      {/* ── Outline ── */}
      <g fill="var(--bg-app)" stroke="var(--border-strong)" strokeWidth={1.5}>
        <circle cx="100" cy="32" r="22" />
        <rect x="92" y="50" width="16" height="16" rx="4" />
        <path d="M 60 64 L 140 64 L 130 170 L 70 170 Z" />
        <rect x="33" y="66" width="22" height="112" rx="10" />
        <rect x="145" y="66" width="22" height="112" rx="10" />
        <rect x="65" y="170" width="32" height="180" rx="12" />
        <rect x="103" y="170" width="32" height="180" rx="12" />
        <ellipse cx="79" cy="357" rx="15" ry="8" />
        <ellipse cx="121" cy="357" rx="15" ry="8" />
      </g>

      {/* ── Clickable zones ── */}
      {zones.map(zone => {
        const isActive = active.includes(zone.id);
        return (
          <g key={zone.id}
            onClick={() => onToggle(zone.id)}
            style={{ cursor: 'pointer' }}
            role="button"
            aria-pressed={isActive}
          >
            {zone.rects.map((r, i) => (
              <rect key={i} x={r.x} y={r.y} width={r.w} height={r.h} rx={6}
                fill={isActive ? 'rgba(239,68,68,0.55)' : 'rgba(148,163,184,0.12)'}
                stroke={isActive ? '#ef4444' : 'var(--border)'}
                strokeWidth={isActive ? 2 : 1}
              />
            ))}
            <title>{zone.label}{isActive ? ' (selected)' : ''}</title>
          </g>
        );
      })}
    </svg>
  );
}

function Legend({ zones, active, onToggle }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
      {zones.map(zone => {
        const isActive = active.includes(zone.id);
        return (
          <button key={zone.id} type="button" onClick={() => onToggle(zone.id)}
            style={{
              padding: '3px 9px', borderRadius: 99, fontSize: '0.68rem', fontFamily: 'inherit',
              border: isActive ? '1px solid #ef4444' : '1px solid var(--border)',
              background: isActive ? 'rgba(239,68,68,0.15)' : 'var(--bg-card)',
              color: isActive ? '#ef4444' : 'var(--text-secondary)',
              cursor: 'pointer', fontWeight: isActive ? 600 : 400,
            }}>
            {zone.label}
          </button>
        );
      })}
    </div>
  );
}

export default function BodyPartSelector({ value, onChange, disabled }) {
  const front = value?.front || [];
  const back = value?.back || [];

  const toggle = (side, id) => {
    if (disabled) return;
    const current = side === 'front' ? front : back;
    const next = current.includes(id) ? current.filter(z => z !== id) : [...current, id];
    onChange({ front: side === 'front' ? next : front, back: side === 'back' ? next : back });
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
      <div>
        <div style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--text-muted)', textAlign: 'center', marginBottom: 4 }}>Front</div>
        <Silhouette side="front" zones={FRONT_ZONES} active={front} onToggle={id => toggle('front', id)} />
        <Legend zones={FRONT_ZONES} active={front} onToggle={id => toggle('front', id)} />
      </div>
      <div>
        <div style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--text-muted)', textAlign: 'center', marginBottom: 4 }}>Back</div>
        <Silhouette side="back" zones={BACK_ZONES} active={back} onToggle={id => toggle('back', id)} />
        <Legend zones={BACK_ZONES} active={back} onToggle={id => toggle('back', id)} />
      </div>
    </div>
  );
}
